import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  CondensedButton,
  Flex,
  FormFieldGroup,
  Grid,
  Heading,
  IconTrashLine,
  List,
  Modal,
  ScreenReaderContent,
  Spinner,
  Text,
  TextInput,
  Tooltip,
  TruncateText,
  View,
} from "@instructure/ui";
import { IconAddLine } from "@instructure/ui-icons";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { OutcomeCategoryService } from "../../../services/OutcomeCategoryService";
import { Card } from "../../Card";
import { sortOutcomes } from "../utils";
import InlineOutcomeCreator from "./inlineAddOutcome";

const CategoriesModalContent = ({
  open,
  setOpen,
  data,
  loading,
  setLoader,
  title,
  service,
  loType,
  program,
  selected,
}) => {
  const { t } = useTranslation();
  const [allOutcomes, setAllOutcomes] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [submitLoad, setSubmitLoad] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [errFlag, setErrFlag] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showOutcomeEditor, setShowOutcomeEditor] = useState(false);
  const [selectedOutcomesFull, setSelectedOutcomesFull] = useState([]);
  const scrollBoxRef = useRef();

  useEffect(() => {
    if (data) {
      const arr = [];
      data.forEach((outcome) => {
        if (!outcome.unapproved) {
          arr.push(outcome);
        }
      });
      setAllOutcomes(arr);
    }
    if (selected) {
      setSelectedOutcomesFull(selected.outcomes);
      const ids = [];
      if (selected.outcomes && selected.outcomes.length > 0) {
        selected.outcomes.forEach((outcome) => {
          if (!outcome.unapproved) {
            ids.push(outcome.id);
          }
        });
      }
      setSelectedOutcomes(ids);
      if (selected.name) {
        setCategoryName(selected.name);
      }
      if (selected.description) {
        setCategoryDescription(selected.description);
      }
      if (selected.id) {
        setCategoryId(selected.id);
      }
    }
  }, [data, selected]);

  const handleCloseModal = () => {
    setErrFlag(false);
    setCategoryDescription("");
    setCategoryName("");
    setSelectedOutcomes([]);
    setOpen(false);
  };

  const updateSelectedOutcomes = (outcomeId) => {
    const index = selectedOutcomes.indexOf(outcomeId);
    if (index === -1) {
      setSelectedOutcomes([...selectedOutcomes, outcomeId]);
    } else {
      setSelectedOutcomes((prevOutcome) => [
        ...prevOutcome.slice(0, index),
        ...prevOutcome.slice(index + 1),
      ]);
    }
  };

  const removeOutcome = (outcomeToRemove) => {
    const newSelectedCourses = selectedOutcomes.filter(
      (outcome) => outcome !== outcomeToRemove
    );
    setSelectedOutcomes(newSelectedCourses);
  };

  const handleSubmit = () => {
    if (categoryName.length < 1 || categoryDescription.length < 1) {
      setErrFlag(true);
    } else {
      setSubmitLoad(true);
      const cat = {};
      cat.name = categoryName;
      cat.description = categoryDescription;
      cat.loIds = selectedOutcomes;
      cat.loType = loType;
      if (categoryId) {
        cat.id = categoryId;
      }
      if (program) {
        cat.programId = program.id;
      }
      service(cat).then(() => {
        setSubmitLoad(false);
        handleCloseModal();
        setLoader(true);
      });
    }
  };

  const refreshLOs = () => {
    const context = loType === "ISLO" ? loType : program.id;
    OutcomeCategoryService.getUncategorizedOutcomesByContext(context).then(
      (unc) => {
        let new_arr =
          context === "ISLO"
            ? unc.institutional_outcomes
            : program.label === "hierarchy level"
              ? unc.level_outcomes
              : unc.program_outcomes;
        if (unc.unapproved_outcomes) {
          new_arr.push(...unc.unapproved_outcomes);
        }
        if (selectedOutcomesFull) {
          new_arr = new_arr.concat(selectedOutcomesFull);
        }
        new_arr = new_arr.filter((o) => !o.unapproved);
        new_arr = sortOutcomes(new_arr);
        setAllOutcomes(new_arr);
        setShowSpinner(false);
      }
    );
  };

  const scrollToBottom = () => {
    scrollBoxRef.current.scrollTo(0, scrollBoxRef.current.scrollHeight);
  };

  return (
    <Modal
      open={open}
      size="large"
      label={title}
      onClose={() => setLoader(true)}
    >
      <Modal.Header>
        <Grid hAlign="space-between">
          <Grid.Row>
            <Grid.Col>
              <Heading level="h2" as="h1">
                {title}
              </Heading>
            </Grid.Col>
            <Grid.Col width="auto">
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={handleCloseModal}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap" alignItems="start">
          {errFlag && (
            <Flex.Item size="100%">
              <Alert variant="warning">
                {t("categories.modalWarningText")}
              </Alert>
            </Flex.Item>
          )}
          <Flex.Item size="50%" padding="small">
            <TextInput
              size="small"
              value={categoryName}
              id="category-name"
              renderLabel={
                <Text size="small">{t("categories.categoryNameText")}</Text>
              }
              onChange={(e) => setCategoryName(e.currentTarget.value)}
            />
          </Flex.Item>
          <Flex.Item size="50%" padding="small">
            <TextInput
              size="small"
              value={categoryDescription}
              id="category-description"
              renderLabel={
                <Text size="small">
                  {t("categories.categoryDescriptionText")}
                </Text>
              }
              onChange={(e) => setCategoryDescription(e.currentTarget.value)}
            />
          </Flex.Item>
          <Flex.Item size="100%">
            <Flex alignItems="start" wrap="wrap">
              <Flex.Item size="50%" padding="small">
                {!loading && !showSpinner ? (
                  <View as="div">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        baselineShift: "center",
                        paddingBottom: "5px",
                        justifyContent: "space-between",
                      }}
                    >
                      <Heading as="h4" level="h4" margin="0 0 small 0">
                        <Text size="small">
                          {t("categories.selectOutcomesText")}
                        </Text>
                      </Heading>
                      <CondensedButton
                        size="small"
                        renderIcon={IconAddLine}
                        color="primary"
                        onClick={() => setShowOutcomeEditor(true)}
                      >
                        <Text size="small">{t("categories.addOutcome")}</Text>
                      </CondensedButton>
                    </div>
                    <Card
                      style={{
                        height: "450px",
                        overflow: "auto",
                        position: "relative",
                      }}
                      ref={scrollBoxRef}
                    >
                      <FormFieldGroup
                        description={
                          <ScreenReaderContent id="groupLabel">
                            {t("categories.selectOutcomesText")}
                          </ScreenReaderContent>
                        }
                        rowSpacing="small"
                      >
                        {allOutcomes.map((outcome, key) => {
                          if (outcome == null) {
                            return null;
                          }
                          return (
                            <View as="div" key={key}>
                              <Checkbox
                                size="small"
                                aria-labelledby={`${outcome.name}Label`}
                                label={
                                  outcome.name.length > 50 ? (
                                    <Tooltip
                                      renderTip={
                                        <div style={{ maxWidth: 600 }}>
                                          {outcome.name}
                                        </div>
                                      }
                                    >
                                      <span id={`${outcome.name}Label`}>
                                        <TruncateText maxLines={1}>
                                          {outcome.name}
                                        </TruncateText>
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <Text size="small">{outcome.name}</Text>
                                  )
                                }
                                value={outcome.id}
                                onChange={(event) =>
                                  updateSelectedOutcomes(event.target.value)
                                }
                                checked={
                                  selectedOutcomes.indexOf(outcome.id) !== -1
                                }
                              />
                            </View>
                          );
                        })}

                        <View as="div" key="addButton">
                          <InlineOutcomeCreator
                            refreshFunction={refreshLOs}
                            outcomeLength={allOutcomes.length}
                            spinnerState={setShowSpinner}
                            shouldShow={showOutcomeEditor}
                            setShouldShow={setShowOutcomeEditor}
                            scrollToBottomFunc={scrollToBottom}
                            spinner={showSpinner}
                            loType={loType}
                            academicSubject={program?.id}
                          />
                        </View>
                      </FormFieldGroup>
                    </Card>
                  </View>
                ) : (
                  <Flex height={450} wrap="wrap">
                    <Flex.Item size="100%">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          baselineShift: "center",
                          paddingBottom: "5px",
                          justifyContent: "space-between",
                        }}
                      >
                        <Heading as="h4" level="h4" margin="0 0 small 0">
                          <Text size="small">
                            {t("categories.selectOutcomesText")}
                          </Text>
                        </Heading>
                        <CondensedButton
                          size="small"
                          renderIcon={IconAddLine}
                          color="primary"
                          disabled
                        >
                          <Text size="small">{t("categories.addOutcome")}</Text>
                        </CondensedButton>
                      </div>
                    </Flex.Item>
                    <Flex.Item shouldGrow>
                      <Skeleton height={470} />
                    </Flex.Item>
                  </Flex>
                )}
              </Flex.Item>
              <Flex.Item size="50%" padding="small">
                <View as="div">
                  <Heading as="h4" level="h4" margin="0 0 small 0">
                    <Text size="small">
                      {t("categories.selectedOutcomesText")}
                    </Text>
                  </Heading>
                  <Card style={{ height: "450px", overflow: "auto" }}>
                    <List isUnstyled>
                      {selectedOutcomes.map((outcomeId, key) => {
                        const outcomeObj = allOutcomes.find(
                          (c) => c.id === outcomeId
                        );
                        const courseName = `${outcomeObj.name}`;
                        if (courseName) {
                          return (
                            <List.Item key={key} margin="0 0 small 0">
                              <Flex
                                justifyItems="space-between"
                                alignItems="end"
                              >
                                <Flex.Item shouldShrink>
                                  {courseName.length > 50 ? (
                                    <Tooltip
                                      renderTip={
                                        <div style={{ maxWidth: 600 }}>
                                          {courseName}
                                        </div>
                                      }
                                    >
                                      <Text size="small">
                                        <TruncateText maxLines={1}>
                                          {courseName}
                                        </TruncateText>
                                      </Text>
                                    </Tooltip>
                                  ) : (
                                    <Text size="small">{courseName}</Text>
                                  )}
                                </Flex.Item>
                                <Flex.Item>
                                  <CondensedButton
                                    onClick={() => removeOutcome(outcomeId)}
                                  >
                                    <ScreenReaderContent>
                                      {t(global.remove) + " " + courseName}
                                    </ScreenReaderContent>
                                    <IconTrashLine />
                                  </CondensedButton>
                                </Flex.Item>
                              </Flex>
                            </List.Item>
                          );
                        }
                      })}
                    </List>
                  </Card>
                </View>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small" onClick={handleCloseModal}>
          {t("global.cancel")}
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitLoad}>
          {submitLoad ? (
            <Spinner renderTitle={t("global.save")} size="x-small" />
          ) : (
            <Text>{t("global.save")}</Text>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CategoriesModalContent;
